import React from 'react';
import LegalLayout from '../components/LegalLayout';
import { AlertTriangle } from 'lucide-react';

export default function DisclaimerPage() {
  return (
    <LegalLayout
      title="Disclaimer"
      lastUpdated="February 2026"
      seoTitle="Disclaimer | PlanningSearch"
      seoDescription="Important disclaimer regarding planning data accuracy, AI-generated content, and limitations of the PlanningSearch platform."
      canonical="https://planningsearch.com/disclaimer"
    >
      <div className="not-prose mb-8 p-5 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800 mb-1">Important Notice</p>
          <p className="text-sm text-amber-700 leading-relaxed">
            PlanningSearch is an information service, not a professional advisory service. The data and insights
            provided should not be treated as a substitute for professional legal, financial, or architectural advice.
          </p>
        </div>
      </div>

      <h2>1. Data Sources & Accuracy</h2>
      <p>
        The planning application data displayed on PlanningSearch is sourced from publicly available council records,
        government databases, and third-party data providers. While we take reasonable care to ensure the information
        is accurate and up to date, we make no warranties or representations as to its completeness, accuracy,
        reliability, or timeliness.
      </p>
      <p>
        Council records may contain errors, omissions, or outdated information. Planning applications can be
        amended, withdrawn, or determined between data updates. We strongly recommend verifying critical details
        directly with the relevant local planning authority before making any business decisions.
      </p>

      <h2>2. AI-Generated Content</h2>
      <p>
        Our AI Intelligence Reports, including but not limited to opportunity scores, estimated project values,
        trade recommendations, approval likelihood assessments, and AI-generated letter content, are produced using
        artificial intelligence models. These outputs are probabilistic estimates and should be treated as indicative
        guidance, not definitive assessments.
      </p>
      <p>
        AI-generated cost estimates are rough approximations based on historical data and general construction
        industry benchmarks. Actual project costs will vary significantly based on specifications, location, market
        conditions, and individual contractor pricing. We do not guarantee the accuracy of any cost figure shown.
      </p>

      <h2>3. Property & Address Information</h2>
      <p>
        Property addresses, applicant names, and related details are reproduced from public planning records.
        PlanningSearch does not independently verify property ownership, boundary information, or the identity of
        planning applicants. Any use of this data for commercial outreach should comply with applicable data
        protection and marketing laws.
      </p>

      <h2>4. Physical Letter Service</h2>
      <p>
        When you use our letter-sending feature, PlanningSearch dispatches physical mail to the address listed on
        the planning application via third-party postal providers. We do not guarantee delivery, nor are we
        responsible for the accuracy of the recipient address as recorded in council records.
      </p>
      <p>
        You are solely responsible for the content of any letters you send through our platform. Letters must not
        contain misleading, defamatory, threatening, or unlawful content. PlanningSearch reserves the right to
        refuse to dispatch any letter that, in our reasonable judgement, violates these standards or applicable law.
      </p>

      <h2>5. No Professional Advice</h2>
      <p>
        Nothing on this platform constitutes professional advice. Specifically:
      </p>
      <ul>
        <li>Cost estimates are not formal quotations or valuations</li>
        <li>Opportunity scores are not investment recommendations</li>
        <li>Approval likelihood is not a legal opinion on planning outcomes</li>
        <li>Trade recommendations are general suggestions, not expert assessments</li>
      </ul>
      <p>
        You should always consult qualified professionals — including surveyors, architects, solicitors, and
        financial advisors — before making decisions based on information obtained through PlanningSearch.
      </p>

      <h2>6. Third-Party Links & Services</h2>
      <p>
        The Service may contain links to third-party websites, APIs, or services. We are not responsible for the
        content, availability, or privacy practices of external sites. Use of third-party services is at your own
        risk and subject to their terms and conditions.
      </p>

      <h2>7. Service Availability</h2>
      <p>
        We aim to maintain continuous availability of the Service but do not guarantee uninterrupted access.
        The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our
        control. We are not liable for any loss arising from periods of unavailability.
      </p>

      <h2>8. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, PlanningSearch Ltd and its directors, employees, and agents
        shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising
        from your use of, or reliance on, the information and services provided through this platform.
      </p>
      <p>
        This includes, without limitation, damages arising from inaccurate data, AI-generated content,
        failed letter deliveries, missed business opportunities, or any other use of the Service.
      </p>

      <h2>9. Updates to This Disclaimer</h2>
      <p>
        We may update this Disclaimer from time to time to reflect changes in our services, data sources,
        or legal requirements. Please review this page periodically.
      </p>

      <h2>10. Contact</h2>
      <p>
        If you have questions about this Disclaimer, please contact us at{' '}
        <a href="mailto:legal@planningsearch.com">legal@planningsearch.com</a>.
      </p>
    </LegalLayout>
  );
}
